import { Currency } from '@search/filter-enums/enums/Currency';
import { formatPrice } from '@search/helpers/src/formatPrice';
import { getDeclensionMonths } from '@search/helpers/src/declension';

import { declensionYear } from '../../declensions';

// Manager: Это по данным РБК недвижимость
const RISE_PRICE_RATE = 7 / 100;
// https://www.cbr.ru/statistics/avgprocstav/ (ЦБ - 1,3%)
const DEPOSIT_RATE = 7.6 - 1.3;
const LIMIT_ACC_YEARS = 40;

export function calcMortgageRate(mortgagePercent: number) {
    return mortgagePercent / 100 / 12;
}

function calcFormula({
    futureCost = 0,
    percent = 10,
    invoice = 0,
    currentCost
}: {
    percent: number;
    futureCost: number;
    invoice: number;
    currentCost: number;
}) {
    const rate = calcMortgageRate(percent);

    // должно быть минус БС, но т.к. мы её не учитываем как 'инвестицию' (т.е. её отбив),
    // то используем только как то, к какой сумме должны прийти
    const numeratorNumeratora = (futureCost * rate + invoice);
    const denominatorNumeratora = (currentCost * rate + invoice);

    return Math.log(numeratorNumeratora / denominatorNumeratora) / Math.log(1 + rate);
}

export const calcMonthPaymentMap = new Map<string, number>();

export function calcMonthPayment({
    years = 30,
    mortgagePercent,
    sumCredit
}: {
    years?: number;
    mortgagePercent: number;
    sumCredit: number;
}) {
    const stupidKey = `${sumCredit}${mortgagePercent}${years}`;

    if (! calcMonthPaymentMap.has(stupidKey)) {
        const mortgageRate = calcMortgageRate(mortgagePercent);
        const countMonths = 12 * years;

        calcMonthPaymentMap.set(stupidKey, (sumCredit * mortgageRate) / (1 - Math.pow(1 + mortgageRate, -countMonths)));
    }
    return calcMonthPaymentMap.get(stupidKey) as number;
}

function formatMonths(monthsFloat: number) {
    let timeYears = Math.trunc(monthsFloat / 12);
    let timeMonths = Math.ceil(12 * (monthsFloat / 12 - timeYears));

    if (timeMonths === 12) {
        timeMonths = 0;
        timeYears++;
    }

    return `за\u00A0${
        timeYears ? `${timeYears}\u00A0${declensionYear(timeYears)}` : ''
    }${timeYears && timeMonths ? ' ' : ''}${
        timeMonths ? getDeclensionMonths(timeMonths) : ''
    }`;
}

function calcAccPeriod({
    currentRateRise: initialRateRise,
    propertyCost,
    currentPropertyCost = propertyCost,
    propertyCostPeriod
}: {
    currentRateRise: number;
    currentPropertyCost?: number;
    propertyCost: number;
    propertyCostPeriod: (propertyCost: number) => number;
}): {
    periodPaymentsWithRise: number;
    riseInPrice: number;
    isOverLimit?: boolean;
} {
    const isFirstYear = initialRateRise === 1;
    const currentRateRise = initialRateRise + RISE_PRICE_RATE;
    // в первый год подорожание не должно быть учитано
    const currentPropertyCostRise = isFirstYear ? propertyCost : currentPropertyCost * (1 + RISE_PRICE_RATE);
    const periodPaymentsWithRise = propertyCostPeriod(currentPropertyCostRise);

    // начисляем проценты подорожания и сравниваем, мы должны накопить больше, чем стоит квартира с учётом подорожания (1)
    // если без удорожания мы будем копить на квартиру 5 лет, значит нам нужно учесть 5 подорожаний минимум (в данном случае сравнение происходит на каждом этапе подорожания)
    // учли - пересчитываем сколько лет будем копить, и так пока не сойдётся условие (1)
    const countYearsWithRise = isFirstYear ? 1 : (currentRateRise - 1) / RISE_PRICE_RATE;
    const periodPaymentsWithRiseInYears = periodPaymentsWithRise / 12;

    // нам нужно понять, что более limitAccYears лет будем копить, поэтому проверяем, что сейчас
    // limitAccYears + 1 итерация, и что будет следующая тоже
    if (
        periodPaymentsWithRiseInYears > countYearsWithRise &&
        Math.trunc(countYearsWithRise + 1) === LIMIT_ACC_YEARS + 1
    ) {
        return {
            periodPaymentsWithRise,
            riseInPrice: currentPropertyCost,
            isOverLimit: true
        };
    }

    if (periodPaymentsWithRiseInYears > countYearsWithRise) {
        return calcAccPeriod({
            currentRateRise,
            currentPropertyCost: currentPropertyCostRise,
            propertyCost,
            propertyCostPeriod
        });
    }

    return {
        periodPaymentsWithRise,
        riseInPrice: currentPropertyCostRise
    };
}

export function calculateBuyOrDie({
    propertyCost,
    initialPayment,
    mortgagePercent,
    comfortInvoice,
    rent
}: {
    propertyCost: number; // стоимость квартиры
    mortgagePercent: number;
    initialPayment: number; // ownAmount
    comfortInvoice: number;
    rent: number;
}) {
    const {
        periodPaymentsWithRise: calculatedDieMonths,
        riseInPrice: calculatedDieFinalCost,
        isOverLimit: isOverLimitAcc
    } = calcAccPeriod({
        currentRateRise: 1,
        propertyCost,
        propertyCostPeriod: (cost) => calcFormula({
            percent: DEPOSIT_RATE,
            invoice: comfortInvoice - rent,
            futureCost: cost,
            currentCost: initialPayment
        })
    });

    const calculatedBuyMonths = calcFormula({
        percent: mortgagePercent,
        invoice: -comfortInvoice,
        futureCost: 0,
        currentCost: propertyCost - initialPayment
    });

    const priceBuy = formatPrice({
        value: (initialPayment + calculatedBuyMonths * comfortInvoice) * 100,
        currency: Currency.RUB,
        noFractional: true,
        displayCurrency: true
    });
    const priceDie = formatPrice({
        value: calculatedDieFinalCost * 100,
        currency: Currency.RUB,
        noFractional: true,
        displayCurrency: true,
        inWords: calculatedDieFinalCost >= 1e7
    });

    const timeBuy = formatMonths(calculatedBuyMonths);
    const timeDie = isOverLimitAcc ? `более ${LIMIT_ACC_YEARS}\u00A0лет` : formatMonths(calculatedDieMonths);
    const typeProfitable = isOverLimitAcc ? 'buy' : (initialPayment + calculatedBuyMonths * comfortInvoice) < calculatedDieFinalCost ? 'buy' : 'die';

    return {
        priceMortgage: priceBuy,
        priceAcc: priceDie,
        timeMortgage: timeBuy,
        timeAcc: timeDie,
        typeProfitable,
        isOverLimitAcc
    };
}
